import { ArrowRight } from "@phosphor-icons/react";
import { Section, PageHero, SectionHeading, CalloutCard } from "@/components/marketing/MarketingUI";
import useSEO from "@/lib/useSEO";

const LANES = [
  {
    name: "Data Lane",
    color: "#1E6FFF",
    steps: [
      { title: "CSV / XLSX ingest", detail: "Upload Collar, Survey, Assay, Geology and QAQC files." },
      { title: "Column auto-map", detail: "Detects industry aliases (BHID, easting, from/to, grade)." },
      { title: "Deep validation", detail: "Coverage, overlaps, negative depths and out-of-hole assays." },
      { title: "QAQC analytics", detail: "Precision, bias, HARD, control-chart envelopes." },
    ],
  },
  {
    name: "Geoscience Lane",
    color: "#F59E0B",
    steps: [
      { title: "Statistical analysis", detail: "Histogram, CDF, Q-Q, log-probability, top-cut suggestion." },
      { title: "Domaining", detail: "Saved queries, lithology dictionaries and per-domain estimation." },
      { title: "Variography", detail: "Experimental variograms and fitted spherical / exponential models." },
      { title: "Block model build", detail: "IDW, NN and Ordinary Kriging with composite lengths." },
    ],
  },
  {
    name: "Economics Lane",
    color: "#10B981",
    steps: [
      { title: "Cut-off strategy", detail: "AI-suggested cut-off ranges based on your grade distribution." },
      { title: "Floating-cone pit", detail: "Slope-constrained pit optimisation with per-block dispatch." },
      { title: "Sensitivity sweep", detail: "Price, mining cost, processing cost and recovery scenarios." },
      { title: "Monte-Carlo NPV", detail: "Triangular distributions with P10 / P50 / P90 outputs." },
    ],
  },
  {
    name: "Reporting Lane",
    color: "#8B5CF6",
    steps: [
      { title: "Narrative engine", detail: "AI-assisted executive, methodology and findings writeups." },
      { title: "CPR assembly", detail: "JORC / NI 43-101 / SAMREC-aligned PDF and DOCX." },
      { title: "Signatures", detail: "Prepared / Reviewed / Approved sign-off blocks with images." },
      { title: "Public share links", detail: "Watermarked PDFs with expiry, view tracking and audit logs." },
    ],
  },
];

export default function WorkflowPage() {
  useSEO({
    title: "Workflow · Four parallel lanes, one deterministic pipeline",
    description: "SRES organises the resource lifecycle into four coordinated lanes — Data, Geoscience, Economics and Reporting — with typed hand-offs between every stage.",
    keywords: "SRES workflow, resource pipeline, geoscience data, reporting standards",
    path: "/workflow",
  });
  return (
    <div data-testid="workflow-page">
      <PageHero
        eyebrow="Workflow"
        title="Four parallel lanes. One deterministic pipeline."
        description="SRES is organised as four coordinated lanes — Data, Geoscience, Economics and Reporting. Each lane owns a well-defined slice of the resource lifecycle, and each hands typed outputs to the next."
        testid="workflow-hero"
      />

      <Section className="py-20 md:py-28" testid="workflow-lanes">
        <div className="space-y-10">
          {LANES.map((lane, li) => (
            <div key={lane.name} className="rounded-2xl border border-slate-200 bg-white p-8" data-testid={`workflow-lane-${li}`}>
              <div className="flex items-center gap-3 mb-6">
                <span
                  className="inline-block w-3 h-3 rounded-full"
                  style={{ background: lane.color }}
                />
                <div className="font-heading font-black text-slate-900 text-2xl tracking-tight">{lane.name}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {lane.steps.map((s, si) => (
                  <div
                    key={s.title}
                    className="relative p-5 rounded-xl border border-slate-100 bg-slate-50 hover:bg-white hover:border-brand-blue/40 transition-colors"
                    data-testid={`workflow-lane-${li}-step-${si}`}
                  >
                    <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-mono">Step {si + 1}</div>
                    <div className="mt-1.5 font-heading font-bold text-slate-900 text-[15px] leading-snug">{s.title}</div>
                    <p className="mt-2 text-slate-600 text-[13px] leading-relaxed">{s.detail}</p>
                    {si < lane.steps.length - 1 ? (
                      <ArrowRight
                        size={16}
                        className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 text-slate-300"
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section className="py-20 md:py-28 bg-[#F7F8FB]" testid="workflow-principles">
        <SectionHeading
          eyebrow="Design principles"
          title="Reproducibility beats convenience. Every time."
          description="A resource statement is a legally consequential document. Our workflow is designed accordingly."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          <PrinciplePanel
            n="01"
            title="Immutable snapshots"
            description="Every block model, pit optimisation and CPR export is snapshotted with its inputs so any past run can be re-downloaded byte-for-byte."
          />
          <PrinciplePanel
            n="02"
            title="Typed hand-offs"
            description="Lanes exchange strongly-typed payloads — not free-form spreadsheets. A pit run cannot silently consume a stale block model."
          />
          <PrinciplePanel
            n="03"
            title="Full audit trail"
            description="Uploads, permission changes, exports and share-link views are all logged into an admin-visible audit trail."
          />
        </div>
      </Section>

      <Section className="py-16 md:py-24" testid="workflow-cta">
        <CalloutCard
          eyebrow="See the pipeline in action"
          title="Watch a real drillhole database flow end-to-end in one session."
          description="A 45-minute walkthrough on your data — from ingest, through kriging, into a signed CPR PDF."
          cta="Request a walkthrough"
          to="/contact"
          testid="workflow-cta-callout"
        />
      </Section>
    </div>
  );
}

function PrinciplePanel({ n, title, description }) {
  return (
    <div className="p-6 rounded-xl bg-white border border-slate-200">
      <div className="text-[12px] font-mono uppercase tracking-[0.24em] text-brand-blue">{n}</div>
      <div className="mt-3 font-heading font-bold text-slate-900 text-xl">{title}</div>
      <p className="mt-2 text-slate-600 text-[14px] leading-relaxed">{description}</p>
    </div>
  );
}
